import { useEffect, useMemo, useState } from 'react';
import tippy, { followCursor, roundArrow } from 'tippy.js';
import 'tippy.js/dist/svg-arrow.css';
import { useAppSelector } from '../../../../stateManagement/hooks/redux';
import RAContent from './tooltipContent/RAContent';
import RASContent from './tooltipContent/RASContent';
import RBLSContent from './tooltipContent/RBLSContent';
import RCSContent from './tooltipContent/RCSContent';
import RPContent from './tooltipContent/RPContent';
import RWWNContent from './tooltipContent/RWWNContent';
import RDSContent from './tooltipContent/RDSContent';
import RDSPContent from './tooltipContent/RDSPContent';
import RWSPODDContent from './tooltipContent/RWSPODDContent';
import PNMTGContent from './tooltipContent/PNMTGContent';
import PNMTContent from './tooltipContent/PNMTContent';
import TrafficIntensityContent from './tooltipContent/TrafficIntensityContent';
import { getMapTooltipBorderColor, getMapTooltipBorderWidth } from './mapTooltipFunctions';
import { formatMapTooltipDisplayData } from './useMapTooltipDisplayData';
import { Loader } from '../../../loaders/Loader';

const mouseLeftButtonClickIconSrc = require('../../../../assets/images/icons/mouse-left-click-icon.svg').default
const successCopyIconSrc = require('../../../../assets/images/icons/success-copy-icon.svg').default

type MapTooltipProps = {
    type?: any;
}

const mapsWithSegmentInfoOnTooltip = ['RoadWorkSegmentsPODD', 'RoadDiagnosticsSegmentsPassport', 'RoadDiagnosticsSegments', 'RoadDiagnosticsSegments', 'RoadWorksWithNormativities', 'RoadAccidentSegments', 'RoadBedLayerSegments', 'RoadCategorySegments', 'HighwayNetworkSegments', "RepairPlan"]
const mapsWithCopyOfTooltip = ['RoadCategorySegments', 'RoadBedLayerSegments', 'HighwayNetworkSegments', 'PathNormativitiesMeasureTypesGroup', 'PathNormativitiesMeasureType', 'RoadDiagnosticsSegmentsPassport']

const typeToMapTooltipContent: Record<string, JSX.Element> = {
    PathNormativitiesMeasureType: <PNMTContent />,
    PathNormativitiesMeasureTypesGroup: <PNMTGContent />,
    RoadWorkSegmentsPODD: <RWSPODDContent />,
    RoadDiagnosticsSegmentsPassport: <RDSPContent />,
    RoadDiagnosticsSegments: <RDSContent />,
    RoadWorksWithNormativities: <RWWNContent />,
    RoadAccidents: <RAContent />,
    RoadAccidentSegments: <RASContent />,
    RoadBedLayerSegments: <RBLSContent />,
    RoadCategorySegments: <RCSContent />,
    RepairPlan: <RPContent />,
    TrafficIntensity: <TrafficIntensityContent />,
};

// Компонент - подсказка для карты 
export function MapTooltip({ type }: MapTooltipProps) {
    const mapTooltip = useAppSelector(state => state.mapTooltip)
    const { mapTooltipIsLoading, tooltipRoadName, tooltipPathName, tooltipKm, tooltipInfoCopied, mapTooltipIsVisible, data } = mapTooltip ?? {}
    const { Position, Ending, Begining } = data ?? {}
    const displayData = useMemo(() => formatMapTooltipDisplayData(data), [data])
    const { Position: formattedPosition, Ending: formattedEnding, Begining: formattedBegining } = displayData ?? {}

    let borderColor = getMapTooltipBorderColor(type)
    let borderWidth = getMapTooltipBorderWidth(type)

    const [instance, setInstance] = useState<any>()

    useEffect(() => {
        const element: any = document.querySelector('#map');
        const template: any = document?.getElementById(`map-tooltip-${type}`);
        const instance = tippy('#map', {
            content: template?.innerHTML,
            followCursor: true,
            allowHTML: true,
            plugins: [followCursor],
            trigger: 'mousemove mouseenter focus click',
            duration: [0, 0],
            arrow: roundArrow,
            zIndex: 1000,
            popperOptions: {
                modifiers: [
                    {
                        name: 'preventOverflow',
                        options: {
                            altAxis: true,
                            tether: false,
                            boundary: element,
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            boundary: element,
                        },
                    },
                    {
                        name: 'offset',
                        options: {
                            offset: ({ placement }: any) => {
                                if (placement === 'bottom') {
                                    return [0, 30];
                                } else {
                                    return [0, 20];
                                }
                            },
                        },
                    },
                ]
            }
        });
        instance[0].disable()
        setInstance(instance)
    }, [])

    useEffect(() => {
        const element: any = document.querySelector('#map');
        const template: any = document?.getElementById(`map-tooltip-${type}`);
        const tippy = element._tippy
        if (tippy && mapTooltipIsVisible) {
            tippy.setContent(template?.innerHTML)
            tippy.enable()
            tippy.show()
        }
        return () => {
            if (instance) {
                instance[0].disable()
            } else {
                tippy.disable()
            }
        }
    }, [mapTooltip])

    const hasStartPosition = Position != null || Begining != null;
    const hasEndPosition = Ending != null;
    const segmentLength = (hasStartPosition && hasEndPosition)
        ? ` ${formattedPosition ?? formattedBegining} - ${formattedEnding} км`
        : 'Не известен';

    return (
        <div id={`map-tooltip-${type}`} style={{ display: 'none' }}>
            <div id='map-tooltip-content' className={`map-tooltip-content-${type}`} style={{ borderColor, borderWidth }}>
                {mapTooltipIsLoading && <Loader />}
                <div className="tooltip-road-name"> {tooltipRoadName ?? 'Дорога отсутствует'}</div>
                <div className="tooltip-path-name"> {tooltipPathName ?? 'Направление не определено'} </div>
                {mapsWithSegmentInfoOnTooltip.includes(type) &&
                    <div className='tooltip-info-row'> Участок: {segmentLength} </div>
                }
                {typeToMapTooltipContent[type]}
                <div className="tooltip-info-row">Км: {tooltipKm ?? 'Не удалось определить'}</div>
                {mapsWithCopyOfTooltip.includes(type) &&
                    <div className="map-tooltip-copy-label-div">
                        <img src={tooltipInfoCopied ? successCopyIconSrc : mouseLeftButtonClickIconSrc} className={tooltipInfoCopied ? "success-copy-img" : ''} />
                        <span className="map-tooltip-copy-text">{tooltipInfoCopied ? 'Информация скопирована' : 'Нажмите, чтобы скопировать'}</span>
                    </div>
                }
            </div>
        </div>
    );
}
